// src/pages/purchase/CreateDirectPO.js
// ZENO ERP — Direct PO Creation (without MRF)
// Accessible by: SM, Owner, Manager
// Tagged: source: "direct" in Firestore
// Same flow as MRF PO: Draft → Approve → GRN
//
// D-291 P3c: every line carries the rate BEFORE GST, GST % and HSN the way
// the vendor will bill it. GST % is pre-filled ONLY from the item's
// gst_percent in Item Master (no silent 18) and is required; "Rate not agreed
// yet" is a per-line tick; an Unregistered vendor's lines are GST 0. The
// screen shows the ordered value (taxable + GST = total) from
// computeOrderValue(); the service re-checks every line (checkPOLines) and
// re-reads the vendor before it saves. Quick Create adds a missing vendor
// without leaving this page (typed lines are kept).

import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection, getDocs,
  query, where
} from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { useCompany } from "../../context/CompanyContext";
import { createPurchaseOrder, calculateLandedCost, fetchRecentPOHistory, getItemRateHistory } from "../services/purchase/purchaseOrderService";
import { computeOrderValue, supplyTypeOf, vendorGstinOf, vendorGstStatus, checkHsn } from "../../utils/gstBill";
import { checkPOLines } from "../../utils/poLines";
import { pickerItems, searchPicker, typedNameLink, itemOptionLabel, ambiguousNameMessage } from "../../utils/itemPicker";
import { qtyStepFor, checkQtyForUnit, hasUnit } from "../../utils/units";
import UnitPicker from "../../components/purchase/UnitPicker";
import QuickCreateVendor from "../../components/quickcreate/forms/QuickCreateVendor";
import QuickCreateModal  from "../../components/quickcreate/QuickCreateModal";

// ── Category allowlist (same as PurchaseOrderDetail) ─────────────────────────
const PURCHASE_ALLOWED_CATEGORIES = ["MATERIAL", "CONSUMABLE", "ASSET"];
function isPurchaseCategory(cat) {
  if (!cat) return true;
  return PURCHASE_ALLOWED_CATEGORIES.includes(cat.toUpperCase().trim());
}

// P3c-7: the unit list lives in units.js (UNIT_OPTIONS) and the Unit box is
// the shared UnitPicker - an Item Master item's unit is read-only.

// One empty item row. GST % starts EMPTY (D-291 P3c) — it is filled from the
// item picked in Item Master, or typed; never a silent "18". `rate` on this
// screen is the rate BEFORE GST; it is saved as the PO line's base_rate.
const emptyRow = (rowKey) => ({
  rowKey, item_id: null, item_name: "", item_code: "", item_category: "",
  unit: "", unit_locked: false, unit_master_missing: false,
  ordered_qty: "", rate: "", rate_not_agreed: false, is_custom_item: false,
  discount_type: "percent", discount_value: "", gst_percent: "", hsn_code: "",
});

// Item Master gst_percent is a number or null (not set) → the box value.
const gstBoxValue = (gst) => (gst === null || gst === undefined || gst === "" ? "" : String(gst));

// P3c-8: one row linked to an Item Master item - used by a click in the list
// AND by a typed name that is exactly that item's name. Code, HSN, GST % and
// the unit (read-only when Item Master has one, P3c-7) come from the item.
const rowFromMaster = (r, master) => {
  const masterHasUnit = hasUnit(master.unit);
  return {
    ...r, item_id: master.item_id || "", item_name: master.item_name, item_code: master.item_code || "",
    item_category: master.item_category || "", unit: masterHasUnit ? master.unit : "",
    unit_locked: masterHasUnit, unit_master_missing: !masterHasUnit, is_custom_item: false,
    hsn_code: master.hsn_code || "", gst_percent: gstBoxValue(master.gst_percent),
  };
};

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CreateDirectPO() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { company } = useCompany();

  // ── Form state ────────────────────────────────────────────────────────────
  const [vendorId,   setVendorId]   = useState("");
  const [vendorName, setVendorName] = useState("");
  const [notes,      setNotes]      = useState("");
  const [items,      setItems]      = useState([emptyRow(`row_${Date.now()}`)]);
  const [showVendorQuick, setShowVendorQuick] = useState(false);

  // Rate Breakdown — freight is one flat amount for the whole PO (a single
  // shipment cost), split across items proportionally when computing
  // landed cost. Reference only — never feeds GRN's own rate.
  const [freightAmount, setFreightAmount] = useState("");

  // Direct Site Purchase (Priority 4) — defaults to false, every
  // existing Direct PO keeps behaving exactly as before.
  const [isSitePurchase, setIsSitePurchase] = useState(false);
  const [projects,          setProjects]          = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");

  // Site Engineers assigned to the selected project (for the optional
  // per-PO pin) — refetched whenever the project selection changes.
  const [projectSiteEngineers, setProjectSiteEngineers] = useState([]);
  const [assignedEngineerIds,  setAssignedEngineerIds]  = useState([]);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [vendors,      setVendors]      = useState([]);
  const [itemsMaster,  setItemsMaster]  = useState([]);
  const [masterLoading, setMasterLoading] = useState(false);
  const itemsMasterRef = useRef([]);

  // Lowest-Rate Suggestion — fetched once (like itemsMaster), matched
  // per-item client-side. Reference only, never auto-fills any field.
  const [poHistory,        setPoHistory]        = useState([]);
  const [openHistoryRowKey, setOpenHistoryRowKey] = useState(null);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState(null);
  const errorRef = useRef(null);
  const [searchState, setSearchState] = useState({});
  // Item-name inputs, keyed by rowKey — used to measure each input's
  // on-screen position so the suggestion dropdown (position: fixed) can
  // line up under it, independent of the table's own horizontal scroll.
  const itemNameInputRefs = useRef({});

  // Close any open suggestion dropdown on scroll (capture phase catches
  // scroll events from the table's own overflow-x-auto wrapper, which
  // don't bubble to window otherwise) — simpler and more robust than
  // repositioning a fixed-position dropdown on every scroll tick.
  useEffect(() => {
    const closeAllSuggestions = () => {
      setSearchState(prev => {
        const next = {};
        for (const key in prev) next[key] = { ...prev[key], open: false };
        return next;
      });
    };
    window.addEventListener("scroll", closeAllSuggestions, true);
    return () => window.removeEventListener("scroll", closeAllSuggestions, true);
  }, []);

  // ── Load vendors ──────────────────────────────────────────────────────────
  useEffect(() => {
    const loadVendors = async () => {
      try {
        const snap = await getDocs(query(
          collection(db, "vendors"),
          where("company_id", "==", currentUser.company_id || "")
        ));
        setVendors(snap.docs.map(d => ({ id: d.id, ...d.data() }))
          // Vendor Master writes `status`, Quick Create writes `is_active`.
          .filter(v => v.status !== "inactive" && v.is_active !== false)
          .sort((a, b) => (a.vendor_name || a.name || "").localeCompare(b.vendor_name || b.name || "")));
      } catch (e) {
        console.error("[CreateDirectPO] loadVendors:", e);
      }
    };
    if (currentUser?.company_id) loadVendors();
  }, [currentUser?.company_id]);

  // ── Load projects (Direct Site Purchase only) ───────────────────────────
  // Site-linked projects only — standing pattern (CORE_RULES §14/§29).
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const snap = await getDocs(query(
          collection(db, "projects"),
          where("company_id", "==", currentUser.company_id || "")
        ));
        setProjects(
          snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(p => p.site_id)
            .sort((a, b) => (a.project_name || "").localeCompare(b.project_name || ""))
        );
      } catch (e) {
        console.error("[CreateDirectPO] loadProjects:", e);
      }
    };
    if (currentUser?.company_id) loadProjects();
  }, [currentUser?.company_id]);

  // ── Load ALL company Site Engineers whenever a project is selected —
  // not just those already assigned to it. This lets a Manager pin (and
  // auto-assign, on save) an engineer to a Direct Site Purchase right
  // here, without a separate trip to the project's Edit modal. Engineers
  // already on the project are tagged for clarity; anyone else picked
  // gets added to the project's site_engineer_ids[] at PO-save time.
  useEffect(() => {
    const loadCompanyEngineers = async () => {
      setAssignedEngineerIds([]);
      if (!selectedProjectId || !currentUser?.company_id) {
        setProjectSiteEngineers([]);
        return;
      }
      try {
        const snap = await getDocs(query(
          collection(db, "users"),
          where("company_id", "==", currentUser.company_id || ""),
          where("role", "==", "site_engineer")
        ));
        setProjectSiteEngineers(
          snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        );
      } catch (e) {
        console.error("[CreateDirectPO] loadCompanyEngineers:", e);
      }
    };
    loadCompanyEngineers();
  }, [selectedProjectId, currentUser?.company_id]);

  // ── Load items master ─────────────────────────────────────────────────────
  useEffect(() => {
    const loadItems = async () => {
      try {
        setMasterLoading(true);
        // P4-3: own + shared "" items, every item listed (names repeat).
        const snap = await getDocs(query(collection(db, "items"), where("company_id", "in", [currentUser.company_id || "", ""])));
        const list = pickerItems(snap.docs.map(d => ({ id: d.id, ...d.data() })), currentUser.company_id || "", {
          allow: (item) => isPurchaseCategory(item.item_category || item.category || null),
        });
        itemsMasterRef.current = list;
        setItemsMaster(list);
      } catch (e) {
        console.error("[CreateDirectPO] loadItems:", e);
      } finally {
        setMasterLoading(false);
      }
    };
    if (currentUser?.company_id) loadItems();
  }, [currentUser?.company_id]);

  // ── Load rate history (Lowest-Rate Suggestion) ────────────────────────────
  useEffect(() => {
    const loadHistory = async () => {
      const history = await fetchRecentPOHistory(currentUser.company_id || "");
      setPoHistory(history);
    };
    if (currentUser?.company_id) loadHistory();
  }, [currentUser?.company_id]);

  // ── Item search ───────────────────────────────────────────────────────────
  const getSuggestions = useCallback((queryStr, rowKey) => {
    // P4-3: "already added" by item_id - a same-name item stays pickable.
    const takenIds = items.filter(r => r.rowKey !== rowKey && r.item_id).map(r => r.item_id);
    return searchPicker(itemsMasterRef.current, queryStr, { takenIds });
  }, [items]);

  const handleSearchChange = useCallback((rowKey, value) => {
    const suggestions = getSuggestions(value, rowKey);
    const isCustom = value.trim().length >= 2 && suggestions.length === 0;
    // P3c-8: the full name of exactly ONE Item Master item typed -> the line
    // is linked to it, as if it was clicked (never a custom line by mistake,
    // so the GRN adds stock to the right item). Not when another row
    // already has that item. P4-3: 2+ items with the name -> stays unlinked,
    // with a message beside the field.
    const link = typedNameLink(itemsMasterRef.current, value);
    const exact = link.item || null;
    const notice = link.ambiguous ? ambiguousNameMessage(link.ambiguous) : (link.blocked || "");
    setItems(prev => {
      const taken = !!exact && prev.some(o => o.rowKey !== rowKey && o.item_id === exact.item_id);
      return prev.map(r => {
        if (r.rowKey !== rowKey) return r;
        if (exact && !taken) return rowFromMaster(r, exact);
        // P3c-7: typing un-picks the Item Master item -> its unit is unlocked
        // and cleared (a custom item picks its own unit).
        return { ...r, item_name: value, item_id: null, item_code: "", is_custom_item: isCustom,
          ...(r.unit_locked || r.unit_master_missing ? { unit: "", unit_locked: false, unit_master_missing: false } : {}) };
      });
    });
    // Measure the input's on-screen position so the fixed-position dropdown
    // (rendered below) lines up under it regardless of the table's own
    // horizontal scroll offset.
    const inputEl = itemNameInputRefs.current[rowKey];
    const rect = inputEl?.getBoundingClientRect();
    setSearchState(prev => ({
      ...prev,
      [rowKey]: {
        query: value, suggestions, open: suggestions.length > 0, notice,
        pos: rect ? { top: rect.bottom, left: rect.left, width: rect.width } : prev[rowKey]?.pos,
      },
    }));
  }, [getSuggestions]);

  const handleSelectSuggestion = useCallback((rowKey, master) => {
    if (master._alreadyAdded || master._blocked) return;
    // D-291 P3c: code, HSN and GST % come from THIS item (Item Master). An
    // item without GST % leaves the box empty — the user types it.
    // P3c-7: the unit too - read-only when Item Master has one, saved exactly
    // as Item Master spells it; without one the user picks it (amber hint).
    setItems(prev => prev.map(r => r.rowKey === rowKey ? rowFromMaster(r, master) : r));
    setSearchState(prev => ({ ...prev, [rowKey]: { query: master.item_name, suggestions: [], open: false } }));
  }, []);

  // ── Item row management ───────────────────────────────────────────────────
  const addRow = () => {
    const rowKey = `row_${Date.now()}`;
    setItems(prev => [...prev, emptyRow(rowKey)]);
    setSearchState(prev => ({ ...prev, [rowKey]: { query: "", suggestions: [], open: false } }));
  };

  const removeRow = (rowKey) => {
    if (items.length === 1) return;
    setItems(prev => prev.filter(r => r.rowKey !== rowKey));
    setSearchState(prev => { const s = { ...prev }; delete s[rowKey]; return s; });
  };

  const updateRow = (rowKey, field, value) => {
    setItems(prev => prev.map(r => r.rowKey === rowKey ? { ...r, [field]: value } : r));
  };

  // ── Vendor GST status (D-291 P3c) ────────────────────────────────────────
  // An Unregistered vendor cannot charge GST → every line is GST 0 (shown
  // locked). The typed GST stays in the row, so switching back to a
  // registered vendor shows it again.
  const selectedVendor     = vendors.find(v => v.id === vendorId) || null;
  const vendorStatus       = selectedVendor ? vendorGstStatus(selectedVendor) : "not_entered";
  const vendorUnregistered = vendorStatus === "unregistered";
  const effectiveGst       = (row) => (vendorUnregistered ? 0 : row.gst_percent);
  // CGST + SGST (same state) or IGST (other state) — null while a GSTIN is missing.
  const supplyType = selectedVendor ? supplyTypeOf(company?.gst_number, vendorGstinOf(selectedVendor)) : null;

  // ── Ordered value (D-291 P3c) — the same computeOrderValue() the PO page
  // and the approval use: qty × rate before GST − discount = taxable, GST
  // per rate. Lines ticked "Rate not agreed yet" are listed, not counted.
  const filledRows = items.filter(i => i.item_name?.trim());
  const orderValue = computeOrderValue(
    filledRows.map(r => ({
      line_id: r.rowKey, item_name: r.item_name, ordered_qty: r.ordered_qty,
      base_rate: r.rate, rate_not_agreed: r.rate_not_agreed === true,
      discount_type: r.discount_type, discount_value: r.discount_value,
      gst_percent: effectiveGst(r),
    })),
    supplyType
  );
  const lineValue = (rowKey) => orderValue.lines.find(l => l.line_id === rowKey) || null;

  // ── Rate Breakdown preview (reference only — never the PO value above).
  // Uses the SAME calculation the backend applies at creation
  // (calculateLandedCost), so what the Manager sees here is what gets stored.
  const landedItems = calculateLandedCost(
    items.map(item => ({
      ...item,
      qty: Number(item.ordered_qty || 0),
      base_rate: item.rate_not_agreed ? 0 : Number(item.rate || 0),
      gst_percent: Number(effectiveGst(item) || 0),
    })),
    Number(freightAmount || 0)
  );
  const landedGrandTotal = landedItems.reduce((sum, item) => sum + (item.landed_total || 0), 0);

  // ── PO number generation ──────────────────────────────────────────────────
  // REMOVED (Single-Procurement-Engine consolidation) — this used to be a
  // local counter here, duplicating the same logic (and the same D-261/
  // D-262 unscoped-query bug class) already in purchaseOrderService.js's
  // own generatePONumber(). createPurchaseOrder() below now generates the
  // PO number centrally — do NOT reintroduce a second counter here.

  // ── Save PO ───────────────────────────────────────────────────────────────
  // CONSOLIDATED (Single-Procurement-Engine fix) — calls the SAME
  // createPurchaseOrder() service function VendorAssignmentModal.js uses,
  // instead of its own inline addDoc(). PO-numbering and custom-item ID
  // generation both now live in exactly one place.
  const handleSave = async () => {
    setError(null);

    const showError = (msg) => {
      setError(msg);
      setTimeout(() => errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    };

    // Validation
    if (!vendorId) { showError("Please select a vendor."); return; }
    if (isSitePurchase && !selectedProjectId) {
      showError("Please select a project for this Direct Site Purchase.");
      return;
    }
    const activeItems = items.filter(i => i.item_name?.trim());
    if (activeItems.length === 0) { showError("Add at least one item."); return; }

    // D-291 P3c: the raw boxes go to the service as typed — an empty GST or
    // rate box is never turned into 0 here. checkPOLines() is the same check
    // the service runs again before it saves; running it here first gives
    // the message before anything is written.
    const payloadItems = activeItems.map((item) => ({
      item_id:         item.item_id || null, // null → service generates a custom_ id
      item_name:       item.item_name.trim(),
      item_code:       item.item_code || "",
      item_category:   item.item_category || "",
      unit:            item.unit || "",
      ordered_qty:     item.ordered_qty,
      base_rate:       item.rate_not_agreed ? "" : item.rate,
      rate_not_agreed: item.rate_not_agreed === true,
      is_custom_item:  item.is_custom_item || !item.item_id,
      discount_type:   item.discount_type === "flat" ? "flat" : "percent",
      discount_value:  item.discount_value,
      gst_percent:     effectiveGst(item),
      hsn_code:        item.hsn_code || "",
    }));
    const lineCheck = checkPOLines(payloadItems, { vendorUnregistered });
    if (!lineCheck.ok) { showError(`Please fix these lines:\n- ${lineCheck.errors.join("\n- ")}`); return; }

    try {
      setSaving(true);
      const vendor = selectedVendor;
      const selectedProject = projects.find(p => p.id === selectedProjectId);

      const response = await createPurchaseOrder({
        company_id:       currentUser.company_id || "",
        vendor_id:        vendorId,
        vendor_name:      vendor?.vendor_name || vendor?.name || vendorName,
        vendor_email:     vendor?.email || "",
        vendor_phone:     vendor?.phone || "",
        vendor_address:   vendor?.address || "",
        // The service re-reads the vendor and saves its GSTIN itself; this is
        // only the screen's copy (old Quick Create vendors keep `gst`).
        vendor_gst:       vendorGstinOf(vendor),
        source:           "direct",         // ← tagged as Direct PO, same as before
        purchase_mode:    isSitePurchase ? "direct_site_purchase" : "stock_purchase",
        project_id:       isSitePurchase ? selectedProjectId : "",
        project_name:     isSitePurchase ? (selectedProject?.project_name || "") : "",
        site_id:          isSitePurchase ? (selectedProject?.site_id || "") : "",
        site_name:        isSitePurchase ? (selectedProject?.site_name || "") : "",
        assigned_site_engineer_ids: isSitePurchase ? assignedEngineerIds : [],
        notes:            notes.trim(),
        created_by:       currentUser.uid,
        created_by_name:  currentUser.name || currentUser.email || "",
        freight_amount:   Number(freightAmount || 0),
        items:            payloadItems,
      });

      if (!response.success) {
        throw response.error || new Error("Purchase order creation failed");
      }

      // If any newly-picked Site Engineer isn't already on the project,
      // add them to the project's own site_engineer_ids[] too — so this
      // isn't just a one-off PO-level pin, it's a real project
      // assignment the Manager can see/manage from the project itself
      // afterward, and future POs on this project benefit from it too.
      // D-291 P3c: done only AFTER the PO is saved, so a refused PO never
      // changes the project.
      if (isSitePurchase && selectedProject && assignedEngineerIds.length > 0) {
        const existing = selectedProject.site_engineer_ids || [];
        const newIds = assignedEngineerIds.filter(id => !existing.includes(id));
        if (newIds.length > 0) {
          try {
            const { updateDoc, doc: firestoreDoc } = await import("firebase/firestore");
            await updateDoc(firestoreDoc(db, "projects", selectedProjectId), {
              site_engineer_ids: [...existing, ...newIds],
            });
          } catch (e) {
            console.error("[CreateDirectPO] auto-assign to project failed (non-blocking):", e);
          }
        }
      }

      console.log("[CreateDirectPO] PO created:", response.po_number, response.po_id);
      navigate(`/purchase-orders/${response.po_id}`);

    } catch (e) {
      console.error("[CreateDirectPO] Error:", e);
      // The service's messages name the line to fix — show them as they are.
      showError(e?.message || "Failed to create PO. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 md:p-6 max-w-[1750px] mx-auto">

      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-700 mb-2 flex items-center gap-1">
          ← Back
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-900">Create Direct Purchase Order</h1>
          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">Direct PO</span>
        </div>
        <p className="text-sm text-gray-500 mt-1">Create a PO directly without MRF. Status will be Draft — requires approval before GRN.</p>
      </div>

      {error && (
        <div ref={errorRef} className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 whitespace-pre-line">
          ⚠ {error}
        </div>
      )}

      {/* Vendor Selection */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
        <h2 className="font-semibold text-gray-800 mb-4 text-sm uppercase tracking-wide">Vendor</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Select Vendor *</label>
            <div className="flex gap-2 items-center">
              <select
                value={vendorId}
                onChange={e => {
                  setVendorId(e.target.value);
                  const v = vendors.find(v => v.id === e.target.value);
                  setVendorName(v?.vendor_name || v?.name || "");
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none"
              >
                <option value="">— Select Vendor —</option>
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>{v.vendor_name || v.name}</option>
                ))}
              </select>
              {/* D-291 P3c: add a missing vendor without leaving this page
                  (the item lines typed so far are kept). Same form as Assign
                  Vendor — saves gst_number + gst_registered. */}
              <button
                type="button"
                onClick={() => setShowVendorQuick(true)}
                title="Add new vendor"
                className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center border border-dashed border-indigo-300 text-indigo-500 bg-indigo-50 hover:bg-indigo-100 transition"
              >
                <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                  <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <QuickCreateModal
              open={showVendorQuick}
              onClose={() => setShowVendorQuick(false)}
              title="Add New Vendor"
            >
              <QuickCreateVendor
                companyId={currentUser?.company_id || ""}
                onCreated={(newVendor) => {
                  setVendors(prev => [...prev, newVendor]
                    .sort((a, b) => (a.vendor_name || a.name || "").localeCompare(b.vendor_name || b.name || "")));
                  setVendorId(newVendor.id);
                  setVendorName(newVendor.vendor_name || "");
                  setShowVendorQuick(false);
                }}
                onCancel={() => setShowVendorQuick(false)}
              />
            </QuickCreateModal>
          </div>
          {selectedVendor && (
            <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm">
              <p className="font-medium text-gray-800">{selectedVendor.vendor_name || selectedVendor.name}</p>
              {selectedVendor.contact_person && <p className="text-gray-500 text-xs">Attn: {selectedVendor.contact_person}</p>}
              {selectedVendor.phone && <p className="text-gray-500 text-xs">📞 {selectedVendor.phone}</p>}
              {selectedVendor.email && <p className="text-gray-500 text-xs">📧 {selectedVendor.email}</p>}
              {vendorStatus === "registered" && (
                <p className="text-gray-500 text-xs">
                  GSTIN: {vendorGstinOf(selectedVendor)}
                  {supplyType === "intra" && " · same state → CGST + SGST"}
                  {supplyType === "inter" && " · other state → IGST"}
                </p>
              )}
              {vendorStatus === "unregistered" && (
                <p className="text-amber-700 text-xs font-medium">Unregistered vendor — no GST on this PO (all lines GST 0).</p>
              )}
              {vendorStatus === "not_entered" && (
                <p className="text-amber-700 text-xs">GSTIN not entered — add it in Vendor Master if the vendor is GST registered.</p>
              )}
              {vendorStatus === "invalid" && (
                <p className="text-red-600 text-xs">GSTIN on file is not valid ({vendorGstinOf(selectedVendor)}) — correct it in Vendor Master.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Direct Site Purchase (Priority 4). Off (default): unchanged —
          received at Store. On: Site Engineer confirms qty, Manager/SM/
          Owner finalizes with rate — see ConfirmSiteReceipt.js /
          FinalizeSitePurchase.js. */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
        <h2 className="font-semibold text-gray-800 mb-4 text-sm uppercase tracking-wide">Delivery</h2>
        <label className="flex items-center gap-3 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={isSitePurchase}
            onChange={e => {
              setIsSitePurchase(e.target.checked);
              if (!e.target.checked) setSelectedProjectId("");
            }}
            className="w-4 h-4"
          />
          <span className="text-sm font-medium text-gray-700">
            This is a Direct Site Purchase — vendor delivers straight to a project site, not to Store
          </span>
        </label>

        {isSitePurchase && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">Select Project *</label>
            <select
              value={selectedProjectId}
              onChange={e => setSelectedProjectId(e.target.value)}
              className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none"
            >
              <option value="">— Select Project —</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.project_name} — {p.site_name}</option>
              ))}
            </select>
            {projects.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">No site-linked projects found.</p>
            )}
            <p className="text-xs text-gray-400 mt-2">
              Site Engineers assigned to this project will confirm received quantity; a Manager/SM/Owner will then finalize rate and vendor payment.
            </p>

            {selectedProjectId && (() => {
              const selectedProject = projects.find(p => p.id === selectedProjectId);
              const projectAssignedIds = selectedProject?.site_engineer_ids || [];
              return (
                <div className="mt-4">
                  <label className="block text-xs text-gray-500 mb-1">
                    Pin to specific Site Engineer(s) — optional
                  </label>
                  {projectSiteEngineers.length === 0 ? (
                    <p className="text-xs text-amber-600">
                      No Site Engineer users exist in your company yet — add one via User Management first.
                    </p>
                  ) : (
                    <>
                      <div className="space-y-1.5 border border-gray-200 rounded-lg p-2 max-w-sm max-h-40 overflow-y-auto">
                        {projectSiteEngineers.map(se => {
                          const checked   = assignedEngineerIds.includes(se.id);
                          const onProject = projectAssignedIds.includes(se.id);
                          return (
                            <label key={se.id} className="flex items-center gap-2 text-sm">
                              <input type="checkbox" checked={checked}
                                onChange={e => setAssignedEngineerIds(prev =>
                                  e.target.checked ? [...prev, se.id] : prev.filter(id => id !== se.id)
                                )} />
                              {se.name || se.email || se.id}
                              {onProject && (
                                <span className="text-[10px] text-green-600 font-medium">✓ on project</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Anyone picked here who isn't already on this project will be added to it automatically. Leave unchecked to let anyone assigned to the project confirm it.
                      </p>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Items — single-line-per-item table. Horizontal scroll (inside the
          card only) on narrow screens instead of wrapping fields onto a
          second row, so the whole breakdown (name → amount) always reads
          as one row. Card itself never widens — width matches every other
          card on this page. */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-800 text-sm uppercase tracking-wide">Items</h2>
          {!masterLoading && itemsMaster.length > 0 && (
            <span className="text-xs text-green-600">✓ {itemsMaster.length} items in catalogue</span>
          )}
          {masterLoading && <span className="text-xs text-blue-500">Loading catalogue...</span>}
        </div>

        {/* overflow-x-auto is back — without it, a wide table (many
            columns) silently bled past the card's edge instead of
            scrolling. The item-search suggestion dropdown below is
            rendered with position: fixed and JS-computed coordinates
            instead of position: absolute, so CSS overflow clipping on
            this wrapper never hides it (overflow only clips fixed-position
            descendants when the ancestor has its own transform/filter/
            contain, which this element doesn't). */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-xs text-gray-500 uppercase">
                <th className="text-left font-medium pb-3 pr-3 min-w-[340px]">Item Name *</th>
                <th className="text-left font-medium pb-3 px-3 w-24">Unit</th>
                <th className="text-right font-medium pb-3 px-3 w-32">Qty *</th>
                <th className="text-right font-medium pb-3 px-3 w-36">Rate before GST (₹) *</th>
                <th className="text-center font-medium pb-3 px-3 w-56">Discount</th>
                <th className="text-right font-medium pb-3 px-3 w-28">GST % *</th>
                <th className="text-left font-medium pb-3 px-3 w-32">HSN</th>
                <th className="text-right font-medium pb-3 px-3 w-32">Landed Rate</th>
                <th className="text-right font-medium pb-3 px-3 w-36">Landed Total</th>
                <th className="text-right font-medium pb-3 px-3 w-28">Taxable</th>
                <th className="pb-3 pl-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const rs = searchState[item.rowKey] || { query: item.item_name || "", suggestions: [], open: false };
                const lv = lineValue(item.rowKey);
                const hsnCheck = checkHsn(item.hsn_code);
                // D-325: a "Rate not agreed yet" line may leave GST % for the bill.
                const gstEmpty = !vendorUnregistered && !item.rate_not_agreed && String(item.gst_percent ?? "").trim() === "";
                return (
                  <tr key={item.rowKey} className="border-t border-gray-100 align-top">
                    {/* Item name search */}
                    <td className="py-2.5 pr-3 relative">
                      <input
                        ref={el => { itemNameInputRefs.current[item.rowKey] = el; }}
                        type="text"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck="false"
                        value={rs.query || item.item_name || ""}
                        onChange={e => handleSearchChange(item.rowKey, e.target.value)}
                        placeholder="Type to search items..."
                        title={rs.query || item.item_name || ""}
                        className="w-full min-w-[340px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none bg-white"
                      />
                      {rs.open && rs.pos && (
                        <div
                          style={{ position: "fixed", top: rs.pos.top + 4, left: rs.pos.left, width: Math.max(rs.pos.width, 280) }}
                          className="bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                          {rs.suggestions.map(s => {
                            const off = s._alreadyAdded || !!s._blocked;
                            return (
                              <div key={s.id} onClick={() => !off && handleSelectSuggestion(item.rowKey, s)}
                                className={`px-3 py-2 text-sm ${off ? "bg-gray-50 cursor-not-allowed text-gray-400" : "hover:bg-indigo-50 cursor-pointer text-gray-800"}`}>
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-medium">{itemOptionLabel(s)}</span>
                                  {s._alreadyAdded && <span className="text-xs text-orange-500 whitespace-nowrap">Already added</span>}
                                </div>
                                {s._blocked
                                  ? <p className="text-xs text-red-500">{s._blocked}</p>
                                  : (s.specification || s.item_category) && (
                                    <p className="text-xs text-gray-400">{[s.specification, s.item_category].filter(Boolean).join(" · ")}</p>
                                  )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {rs.notice && <p className="text-xs text-amber-600 mt-0.5">{rs.notice}</p>}
                      {item.is_custom_item && item.item_name && (
                        <p className="text-xs text-purple-500 mt-0.5 whitespace-nowrap">⚡ Custom</p>
                      )}
                      {item.item_id && (
                        <p className="text-xs text-green-600 mt-0.5 whitespace-nowrap">✓ Item Master · {item.item_id}</p>
                      )}
                      {/* Lowest-Rate Suggestion — reference only, never
                          auto-fills Rate/Discount/GST above. Shown only
                          when past PO history exists for this item. */}
                      {item.item_name?.trim() && (() => {
                        const history = getItemRateHistory(poHistory, item);
                        if (history.length === 0) return null;
                        const isOpen = openHistoryRowKey === item.rowKey;
                        return (
                          <div className="mt-1">
                            <button type="button"
                              onClick={() => setOpenHistoryRowKey(isOpen ? null : item.rowKey)}
                              className="text-xs text-teal-600 hover:text-teal-800 font-medium whitespace-nowrap">
                              📊 {history.length} past rate{history.length > 1 ? "s" : ""} — from ₹{history[0].landed_rate.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                            </button>
                            {isOpen && (
                              <div className="mt-1 border border-gray-200 rounded-lg bg-gray-50 p-2 space-y-1 min-w-[280px]">
                                {history.map((h, i) => (
                                  <div key={i} className={`flex items-center justify-between text-xs px-2 py-1 rounded ${i === 0 ? "bg-teal-50 font-semibold text-teal-800" : "text-gray-600"}`}>
                                    <span className="truncate">
                                      {h.vendor_name} · {h.created_at?.toDate ? h.created_at.toDate().toLocaleDateString("en-IN") : "—"}
                                      {i === 0 && <span className="ml-1 text-teal-600">(lowest)</span>}
                                    </span>
                                    <span className="whitespace-nowrap ml-2">₹{h.landed_rate.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>

                    {/* Unit — P3c-7: shared UnitPicker. Item Master item with a
                        unit -> read-only (saved as Item Master spells it);
                        custom item / Item Master item without a unit -> the
                        shared list (units.js); a blank unit is refused on save. */}
                    <td className="py-2.5 px-3">
                      <UnitPicker value={item.unit} locked={item.unit_locked}
                        masterNoUnit={item.unit_master_missing}
                        onChange={(u) => updateRow(item.rowKey, "unit", u)} />
                    </td>

                    {/* Qty — sized for a 5-digit value (up to 99999). P3c-6: counted
                        units (Nos, Set, Pcs ...) take whole numbers only; measured
                        units (Mtr, RM, SqM, Kg ...) up to 2 decimals (units.js). */}
                    <td className="py-2.5 px-3">
                      {(() => {
                        const qtyCheck = item.ordered_qty !== "" && item.ordered_qty != null
                          ? checkQtyForUnit(item.ordered_qty, item.unit) : { ok: true, reason: "" };
                        return (
                          <>
                            <input type="number" min={qtyStepFor(item.unit)} step={qtyStepFor(item.unit)} value={item.ordered_qty || ""} onChange={e => updateRow(item.rowKey, "ordered_qty", e.target.value)}
                              placeholder="0" className={`w-full min-w-[120px] px-3 py-2 border rounded-lg text-sm text-right focus:ring-2 focus:ring-indigo-300 outline-none bg-white ${
                                qtyCheck.ok ? "border-gray-300" : "border-red-400"
                              }`} />
                            {!qtyCheck.ok && <p className="text-[11px] text-red-600 mt-0.5">{qtyCheck.reason}</p>}
                          </>
                        );
                      })()}
                    </td>

                    {/* Rate before GST — sized for a 7-digit value (up to 9999999).
                        D-291 P3c: "Rate not agreed yet" = the rate comes on
                        the bill; the line is saved with base_rate 0 on purpose
                        and shown as "Rate not agreed" (no GRN pre-fill). */}
                    <td className="py-2.5 px-3">
                      <input type="number" min="0.01" step="0.01"
                        value={item.rate_not_agreed ? "" : (item.rate || "")}
                        onChange={e => updateRow(item.rowKey, "rate", e.target.value)}
                        disabled={item.rate_not_agreed}
                        placeholder={item.rate_not_agreed ? "Not agreed" : "0"}
                        className="w-full min-w-[160px] px-3 py-2 border border-gray-300 rounded-lg text-sm text-right focus:ring-2 focus:ring-indigo-300 outline-none bg-white disabled:bg-gray-100" />
                      <label className="flex items-center gap-1.5 mt-1 text-xs text-gray-500 whitespace-nowrap cursor-pointer">
                        <input type="checkbox" checked={item.rate_not_agreed === true}
                          onChange={e => updateRow(item.rowKey, "rate_not_agreed", e.target.checked)} />
                        Rate not agreed yet
                      </label>
                    </td>

                    {/* Discount — width follows the mode: % tops out at 100
                        (3 digits) so stays narrow; flat ₹ can realistically
                        run into 6 digits (subtotal-sized), so widens the
                        moment the dropdown switches to ₹. max=100 for %
                        stays, so a mistyped value can never silently blow
                        up the landed-cost calculation */}
                    <td className="py-2.5 px-3">
                      <div className="flex gap-1.5">
                        <select
                          value={item.discount_type || "percent"}
                          onChange={e => updateRow(item.rowKey, "discount_type", e.target.value)}
                          className="px-2 py-2 border border-gray-300 rounded-lg text-sm outline-none bg-white shrink-0">
                          <option value="percent">%</option>
                          <option value="flat">₹</option>
                        </select>
                        <input type="number" min="0"
                          max={item.discount_type === "flat" ? undefined : 100}
                          step="0.01" value={item.discount_value || ""}
                          onChange={e => updateRow(item.rowKey, "discount_value", e.target.value)}
                          placeholder="0"
                          className={`px-3 py-2 border border-gray-300 rounded-lg text-sm text-right outline-none focus:ring-2 focus:ring-indigo-300 bg-white ${
                            item.discount_type === "flat" ? "w-[140px]" : "w-[90px]"
                          }`} />
                      </div>
                    </td>

                    {/* GST % — D-291 P3c: REQUIRED, 0 to 40. Pre-filled only
                        from the item's GST % in Item Master; never a silent
                        18 (an empty box is refused on save, not read as 0).
                        D-325: blank allowed on a "Rate not agreed yet" line
                        = decided on the vendor's bill (saved as null).
                        Unregistered vendor → locked at 0. */}
                    <td className="py-2.5 px-3">
                      <input type="number" min="0" max="40" step="0.01"
                        value={vendorUnregistered ? "0" : (item.gst_percent ?? "")}
                        onChange={e => updateRow(item.rowKey, "gst_percent", e.target.value)}
                        disabled={vendorUnregistered}
                        placeholder={item.rate_not_agreed ? "On bill" : "GST"}
                        title={vendorUnregistered
                          ? "Unregistered vendor — no GST"
                          : item.rate_not_agreed
                          ? "Leave blank if the vendor's bill will decide GST. Type 0 only if this item truly has no GST."
                          : "GST % as the vendor will bill it"}
                        className={`w-full min-w-[80px] px-3 py-2 border rounded-lg text-sm text-right outline-none focus:ring-2 focus:ring-indigo-300 bg-white disabled:bg-gray-100 ${
                          gstEmpty && item.item_name?.trim() ? "border-amber-400" : "border-gray-300"
                        }`} />
                      {gstEmpty && item.item_name?.trim() && (
                        <p className="text-[11px] text-amber-600 mt-0.5 whitespace-nowrap">Type GST %</p>
                      )}
                    </td>

                    {/* HSN — optional, copied from Item Master; 4, 6 or 8 digits */}
                    <td className="py-2.5 px-3">
                      <input type="text" inputMode="numeric" maxLength={8} value={item.hsn_code || ""}
                        onChange={e => updateRow(item.rowKey, "hsn_code", e.target.value.replace(/\s+/g, ""))}
                        placeholder="Optional"
                        className={`w-full min-w-[100px] px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-300 bg-white ${
                          hsnCheck.ok ? "border-gray-300" : "border-red-400"
                        }`} />
                      {!hsnCheck.ok && <p className="text-[11px] text-red-600 mt-0.5">{hsnCheck.reason}</p>}
                    </td>

                    {/* Landed Rate — reference only */}
                    <td className="py-2.5 px-3 text-right text-sm font-semibold text-teal-700 whitespace-nowrap">
                      ₹{(landedItems[idx]?.landed_rate || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </td>

                    {/* Landed Total — reference only */}
                    <td className="py-2.5 px-3 text-right text-sm font-semibold text-teal-700 whitespace-nowrap">
                      ₹{(landedItems[idx]?.landed_total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </td>

                    {/* Taxable — qty × rate before GST − discount (computeOrderValue) */}
                    <td className="py-2.5 px-3 text-right text-sm font-semibold text-gray-700 whitespace-nowrap">
                      {lv?.status === "ok" && `₹${inr(lv.taxable)}`}
                      {lv?.status === "rate_not_agreed" && <span className="text-xs text-amber-600">Rate not agreed</span>}
                      {lv?.status === "rate_missing" && <span className="text-xs text-gray-400">—</span>}
                      {lv?.status === "error" && <span className="text-xs text-red-600">Check line</span>}
                    </td>

                    {/* Remove */}
                    <td className="py-2.5 pl-3 text-right">
                      {items.length > 1 && (
                        <button onClick={() => removeRow(item.rowKey)} className="text-gray-300 hover:text-red-500 text-xs" title="Remove item">✕</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <button onClick={addRow} className="mt-4 text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1">
          + Add Item
        </button>
      </div>

      {/* Rate Breakdown — Freight (one flat amount for the whole PO) */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
        <h2 className="font-semibold text-gray-800 text-sm uppercase tracking-wide mb-3">Rate Breakdown — Freight</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Freight / Transport (₹, whole PO)</label>
            <input type="number" min="0" step="0.01" value={freightAmount}
              onChange={e => setFreightAmount(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none" />
            <p className="text-xs text-gray-400 mt-1">Split across items proportionally — reference only, never changes Rate/Amount or the GRN rate.</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Landed Cost (all items, ref. only)</p>
            <p className="text-lg font-bold text-teal-700">
              ₹{landedGrandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
        <h2 className="font-semibold text-gray-800 text-sm uppercase tracking-wide mb-3">Notes (Optional)</h2>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
          placeholder="Delivery instructions, payment terms, or any other notes..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 outline-none resize-none" />
      </div>

      {/* Total + Submit */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-start justify-between mb-5 gap-4">
          {/* D-291 P3c: the ordered value the approver will see — taxable +
              GST (CGST + SGST or IGST once both GSTINs are known) = total.
              Freight above is reference only and not in this value. */}
          <div className="text-sm">
            <p className="text-gray-500">Order Value</p>
            <div className="mt-1 space-y-0.5 text-gray-700">
              <p>Taxable: <span className="font-semibold">₹{inr(orderValue.taxable_total)}</span></p>
              {orderValue.supply_type === "intra" && (
                <p>CGST ₹{inr(orderValue.cgst)} + SGST ₹{inr(orderValue.sgst)}</p>
              )}
              {orderValue.supply_type === "inter" && <p>IGST ₹{inr(orderValue.igst)}</p>}
              {!orderValue.supply_type && (
                <p>GST ₹{inr(orderValue.tax_total)} <span className="text-xs text-gray-400">(CGST / SGST or IGST is decided on the bill)</span></p>
              )}
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-1">₹{inr(orderValue.total)}</p>
            {orderValue.rate_not_agreed.length > 0 && (
              <p className="text-xs text-amber-600 mt-1">
                {orderValue.rate_not_agreed.length} line{orderValue.rate_not_agreed.length > 1 ? "s" : ""} with rate not agreed — not in this value.
              </p>
            )}
            {(orderValue.rate_missing.length > 0 || orderValue.errors.length > 0) && (
              <p className="text-xs text-red-600 mt-1">Some lines are not complete yet — they are not in this value.</p>
            )}
          </div>
          <div className="text-right text-xs text-gray-400">
            <p>{items.filter(i => i.item_name?.trim()).length} items</p>
            <p className="mt-0.5">Status: <span className="font-semibold text-gray-600">Draft</span></p>
            <p className="mt-0.5">Source: <span className="font-semibold text-purple-600">Direct PO</span></p>
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={() => navigate(-1)} disabled={saving}
            className="px-5 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 transition">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !vendorId}
            className="flex-1 px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition">
            {saving ? "Creating PO..." : "Create Draft PO →"}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-3 text-center">
          PO will be saved as Draft. SM / Owner must approve before GRN is allowed.
        </p>
      </div>
    </div>
  );
}